// kiosk-mode.js - "shared preview" (read-only) mode for ?share={token} links.
//
// A shared link opens the app for an ANONYMOUS visitor (no JWT, so every gated
// server call is rejected) and shows EXACTLY ONE target (view / HPC engine /
// Pro module / plugin) in READ-ONLY mode:
//   - inputs are locked (can view, cannot change),
//   - only the Run/Compute button works,
//   - any other interaction (secondary buttons, modal triggers) is intercepted
//     and routed to a Register prompt,
//   - access is gated live by the token: a revoke/remove (410) locks the page
//     on the next run or heartbeat.
// The target's frozen input snapshot is applied first, so the visitor re-runs the
// exact configuration the admin shared - a verifiable proof, not tool access.
//
// All kiosk UI (banner, register prompt, lock-out overlay, loading splash, styles)
// is injected here so no app HTML changes are needed for the visitor side.

import { api, clearTokens } from './api.js';
import { enterKiosk } from './auth.js';
import * as events from './events.js';
import { mountSubscribe } from './verify-subscribe.js';

const PARAM = 'share';
let TOKEN = null;
let locked = false;
let heartbeat = null;

export function kioskToken() { try { return new URLSearchParams(location.search).get(PARAM); } catch { return null; } }
export function isKiosk() { return !!kioskToken(); }

// Sync, called as early as possible to suppress the landing flash before the
// async resolve runs.
export function preKiosk() {
  if (!isKiosk()) return false;
  document.documentElement.classList.add('kiosk-mode');
  injectStyles();
  showSplash();
  return true;
}

// Async, called AFTER all module init_ui (so views/forms/plugins exist).
export async function initKioskMode() {
  TOKEN = kioskToken();
  if (!TOKEN) return false;
  try { clearTokens(); } catch {}                 // never use a stored session in a shared preview

  const res = await api.resolveShare(TOKEN);
  if (!res.ok) { hideSplash(); showLockScreen(res.status === 410 ? 'This shared link is no longer active.' : 'This shared link was not found.'); return true; }

  const target = (res.data && res.data.target) || '';
  window.__kioskTarget = target;   // binds openById/navigation to this one target (no lateral pivot)
  window.__kioskShareToken = TOKEN; // authorizes the gated plugin-code fetch (?share=) for this kiosk
  let params = {};
  try { params = JSON.parse((res.data && res.data.params) || '{}'); } catch {}

  try { enterKiosk(); } catch (e) { /* fall through */ }
  stripChrome();

  const ok = await navigateTo(target);
  hideSplash();
  if (!ok) { showLockScreen('This shared item is unavailable.'); return true; }

  injectBanner(res.data && res.data.label);
  // let the panel render, then apply the frozen config + lock it
  setTimeout(() => { try { applyAndLock(target, params); } catch (e) {} }, 350);
  startGating();
  return true;
}

// ---- navigation: address any view / engine / module / plugin ----------------
function show(view) { try { window.gdbsShowView && window.gdbsShowView(view); } catch {} }

async function navigateTo(target) {
  const i = target.indexOf(':');
  const kind = i < 0 ? target : target.slice(0, i);
  const rest = i < 0 ? '' : target.slice(i + 1);
  if (kind === 'view') { show(rest); return !!document.getElementById('view-' + rest); }
  if (kind === 'hpc') {
    show('hpc');
    const btn = document.querySelector('.hpc-tab[data-ht="' + cssAttr(rest) + '"]');
    if (btn) { btn.click(); return true; }
    return false;
  }
  if (kind === 'pro') {
    const btn = document.querySelector('.adv-tab-btn[data-tab="' + cssAttr(rest) + '"]');
    if (btn) { const v = btn.closest('.view'); if (v && v.id.startsWith('view-')) show(v.id.slice(5)); btn.click(); return true; }
    return false;
  }
  if (kind === 'plugin') {
    const j = rest.indexOf(':'); if (j < 0) return false;
    const tier = rest.slice(0, j), id = rest.slice(j + 1);
    try { if (window.gdbsPlugins && window.gdbsPlugins.openById) return await window.gdbsPlugins.openById(tier, id); } catch {}
    return false;
  }
  return false;
}

function targetPanel(target) {
  const i = target.indexOf(':'); const kind = i < 0 ? target : target.slice(0, i); const rest = i < 0 ? '' : target.slice(i + 1);
  if (kind === 'view') return document.getElementById('view-' + rest);
  if (kind === 'hpc') return document.getElementById('hp-' + rest);
  if (kind === 'pro') return document.getElementById('adv-tab-' + rest);
  if (kind === 'plugin') return document.getElementById('plugin-open-body') || document.getElementById('view-plugin-open');
  return null;
}

// ---- read-only: apply frozen params, lock inputs, guard interactions --------
function applyAndLock(target, params) {
  const panel = targetPanel(target);
  if (!panel) return;
  // unlock any entitlement gate INSIDE the target so it renders for the guest
  panel.querySelectorAll('[id$="-gate"]').forEach(g => g.classList.add('hidden'));
  panel.querySelectorAll('[id$="-content"]').forEach(c => c.classList.remove('hidden'));
  // apply the admin's frozen input snapshot
  Object.keys(params || {}).forEach(k => {
    const el = byKeyIn(panel, k); if (!el) return;
    try { if (el.type === 'checkbox') el.checked = !!params[k]; else el.value = params[k]; el.dispatchEvent(new Event('change', { bubbles: true })); } catch {}
  });
  // LOCK every input (view-only)
  panel.querySelectorAll('input, select, textarea').forEach(el => { el.disabled = true; el.classList.add('kiosk-locked'); });
  installGuard(panel);
  // AUTO-RUN once: the verify link is a self-reproducing proof, so the frozen
  // config genuinely re-executes (std plugins are pure closed-form JS -> the same
  // result + same verdict, a real recomputation, never a cached echo). Guarded so
  // it fires a single time even if applyAndLock is invoked more than once.
  if (!window.__kioskAutoRan) {
    window.__kioskAutoRan = true;
    setTimeout(() => autoRun(panel), 250);
  }
}

// Find the panel's Run control and fire it through the same live-gated path a
// visitor click would take (gateRun -> hit/{token} live-gate + run-count ->
// fireRun re-dispatch). No cached image: the plugin re-runs on the frozen inputs.
function autoRun(panel) {
  try {
    const btn = Array.from(panel.querySelectorAll('button, input[type="submit"], [role="button"], .btn-primary')).find(isRunControl);
    if (btn) gateRun(btn);
  } catch (e) { /* auto-run is best-effort; the manual Run control still works */ }
  // After the run is dispatched, watch the panel so (i) the in-app GTI tooltip is
  // neutralized to a verdict-only string on this public surface and (ii) the
  // Subscribe card is injected once the first result renders.
  try { watchPanel(panel); } catch (e) {}
}

// PUBLIC VERDICT SANITIZE (shared-surface constraint: never expose the word
// 'trust'). The plugin's own trustRegion renders the GTI badge whose tooltip
// title is 'GeoNum Trust Index'. On the shared surface we rewrite any such title
// to the verdict-only value (the right badge segment's text, e.g. 'PRECISE').
// geonum-trust.js is deliberately NOT edited; in-app internals keep their names.
function sanitizeVerdict(panel) {
  try {
    panel.querySelectorAll('.geonum-trust[title], .geonum-trust [title]').forEach(el => {
      const t = el.getAttribute('title');
      if (t && /trust/i.test(t)) {
        const wrap = el.closest('.geonum-trust');
        const seg = wrap && wrap.querySelector('span:last-of-type');
        el.setAttribute('title', (seg && seg.textContent.trim()) || 'GTI verdict');
      }
    });
  } catch (e) {}
}

let subscribeMounted = false;
// One MutationObserver on the target panel: on every mutation it re-sanitizes the
// verdict tooltip (badges re-render after the async run) and, the first time a
// result surfaces, injects the Subscribe card so the shared badge is both a
// verify token and a follow button.
function watchPanel(panel) {
  if (panel.__kioskWatched) return; panel.__kioskWatched = true;
  const run = () => {
    sanitizeVerdict(panel);
    if (!subscribeMounted && hasResult(panel)) injectSubscribe(panel);
  };
  try {
    const mo = new MutationObserver(run);
    mo.observe(panel, { childList: true, subtree: true, attributes: true, attributeFilter: ['title'] });
  } catch (e) {}
  run();   // catch a result/badge that is already present
}

// The verdict badge is the honest signal that a result exists on the surface;
// fall back to any rendered readout/result region so the Subscribe card appears
// even for plugins that do not emit a GTI badge.
function hasResult(panel) {
  try {
    if (panel.querySelector('.geonum-trust')) return true;
    return !!panel.querySelector('[id$="-readout"], [id$="-result"], [id$="-results"], .plugin-result, .result-card');
  } catch (e) { return false; }
}

// SUBSCRIBE: a light card below the panel output that reuses the shared subscribe
// module (POST /api/email/subscribe, double opt-in). Mounted exactly once.
function injectSubscribe(panel) {
  if (subscribeMounted) return; subscribeMounted = true;
  try {
    const card = document.createElement('div');
    card.id = 'kiosk-subscribe';
    card.style.cssText = 'margin:16px auto 4px; max-width:520px; background:#0f172a; border:1px solid #334155; '
      + 'border-radius:12px; padding:16px 18px; color:#e2e8f0; box-shadow:0 8px 30px rgba(0,0,0,.35);';
    card.innerHTML =
      '<div style="font-weight:700; font-size:14px; color:#f1f5f9; margin-bottom:4px;">'
      + '<i class="fas fa-envelope" style="color:#a78bfa; margin-right:6px;"></i>Get new results like this</div>'
      + '<div style="font-size:12px; color:#94a3b8; margin-bottom:12px; line-height:1.5;">'
      + 'This result re-ran live in your browser from a frozen configuration. Follow for new verifiable runs. Double opt-in - we send a confirmation email.</div>'
      + '<div id="kiosk-subscribe-mount"></div>';
    const parent = panel.parentNode || document.body;
    parent.insertBefore(card, panel.nextSibling);
    const mount = card.querySelector('#kiosk-subscribe-mount');
    mountSubscribe(mount, { source: 'verify' });
  } catch (e) { subscribeMounted = false; /* allow a later mutation to retry */ }
}

function byKeyIn(panel, k) {
  try { const byId = panel.querySelector('#' + cssId(k)); if (byId) return byId; } catch {}
  try { return panel.querySelector('[name="' + cssAttr(k) + '"]'); } catch { return null; }
}

function isRunControl(btn) {
  if (!btn) return false;
  if (btn.classList && (btn.classList.contains('btn-primary') || btn.classList.contains('kiosk-allow'))) return true;
  const t = (btn.textContent || '').toLowerCase();
  return /\b(run|compute|solve|calculat|evaluat|simulat|generat|render|play)\b/.test(t);
}

// Allow Run (gated by a live token check); route every OTHER actionable click to
// the Register prompt. Run is intercepted, awaits hit() (counts the run AND live-
// gates), and only then re-dispatches the click so the engine actually runs. This
// makes revoke real for the cooperative 99%: a revoked token (410) blocks the run.
let lastGate = 0;
function installGuard(panel) {
  panel.addEventListener('click', e => {
    if (locked) return;
    const ctrl = e.target.closest('button, a, select, input[type="button"], input[type="submit"], [role="button"]');
    if (!ctrl) return;                 // charts/canvas/text: allow (no-op)
    if (isRunControl(ctrl)) {
      if (ctrl.__kioskCleared) { ctrl.__kioskCleared = false; return; }   // re-dispatched run: let it through
      e.preventDefault(); e.stopImmediatePropagation();
      gateRun(ctrl);
      return;
    }
    e.preventDefault(); e.stopPropagation(); promptRegister();
  }, true);
}
async function gateRun(ctrl) {
  if (locked || !TOKEN) return;
  const now = Date.now();
  // anti-hammer: within 2s of a cleared run, skip the round-trip and just run
  if (now - lastGate < 2000) { fireRun(ctrl); return; }
  const r = await api.hitShare(TOKEN);          // counts the run AND live-gates (410 = revoked/removed)
  if (!r.ok && r.status === 410) { lockOut(); return; }
  lastGate = Date.now();
  fireRun(ctrl);
}
function fireRun(ctrl) { try { ctrl.__kioskCleared = true; ctrl.click(); } catch (e) { ctrl.__kioskCleared = false; } }

// ---- live gating: heartbeat + per-run ---------------------------------------
function startGating() {
  // Run-counting + the live revoke gate happen on the Run click (gateRun). Here
  // we only watch an idle open page so it locks soon after a revoke/remove even
  // if the visitor never clicks Run.
  //
  // Event-driven: subscribe to a server 'revoke' push, scoped to THIS share token
  // (the kiosk has no gdbs_token, so the stream authenticates with the share
  // token as a query param). A push triggers an immediate authoritative recheck.
  try {
    events.connect({ query: { share: TOKEN } });
    events.on('revoke', () => { liveCheck(); });
  } catch (_) { /* fall through to the safety timer */ }
  // Slow safety timer: if the push stream is unavailable, an idle page still
  // locks within ~60s of a revoke. (Was 30s; the event path makes revoke prompt
  // when the stream is up, so the timer can be slower.) Plus on-focus/visibility.
  heartbeat = setInterval(liveCheck, 60000);
  window.addEventListener('focus', liveCheck);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) liveCheck(); });
}
async function liveCheck() { if (locked || !TOKEN) return; const r = await api.checkShare(TOKEN); if (!r.ok && (r.status === 410 || r.status === 404)) lockOut(); }

// ---- chrome stripping -------------------------------------------------------
function stripChrome() {
  hideAll('.sidebar');
  hideAll('.topbar-right');
  hideAll('#system-notice');
  hideAll('.user-menu-wrap');
}
function hideAll(sel) { document.querySelectorAll(sel).forEach(el => { el.style.display = 'none'; }); }

// ---- injected UI ------------------------------------------------------------
function regUrl() { return location.origin + '/#register'; }

function injectBanner(label) {
  if (document.getElementById('kiosk-banner')) return;
  const b = document.createElement('div');
  b.id = 'kiosk-banner';
  b.innerHTML =
    '<span class="kiosk-tag"><i class="fas fa-eye"></i> Shared preview - read only</span>'
    + (label ? '<span class="kiosk-label">' + esc(label) + '</span>' : '')
    + '<a class="kiosk-cta" href="' + regUrl() + '">Register for full access</a>';
  document.body.appendChild(b);
  document.body.classList.add('kiosk-has-banner');
}

function promptRegister() {
  let m = document.getElementById('kiosk-reg');
  if (!m) {
    m = document.createElement('div');
    m.id = 'kiosk-reg'; m.className = 'kiosk-overlay';
    m.innerHTML =
      '<div class="kiosk-card">'
      + '<i class="fas fa-lock kiosk-ic"></i>'
      + '<h3>This is a read-only preview</h3>'
      + '<p>You can run this shared result, but editing inputs and the rest of the platform need an account. A free seat lets you run your own problems.</p>'
      + '<div class="kiosk-actions"><a class="kiosk-btn-primary" href="' + regUrl() + '">Register for a free seat</a>'
      + '<button class="kiosk-btn" type="button" id="kiosk-reg-close">Keep viewing</button></div>'
      + '</div>';
    document.body.appendChild(m);
    m.addEventListener('click', e => { if (e.target === m) m.classList.add('hidden'); });
    m.querySelector('#kiosk-reg-close').addEventListener('click', () => m.classList.add('hidden'));
  }
  m.classList.remove('hidden');
}

function lockOut() {
  if (locked) return; locked = true;
  if (heartbeat) clearInterval(heartbeat);
  try { events.disconnect(); } catch (_) {}  // stop the push stream once revoked
  const o = document.createElement('div');
  o.className = 'kiosk-overlay kiosk-hard';
  o.innerHTML =
    '<div class="kiosk-card">'
    + '<i class="fas fa-link-slash kiosk-ic"></i>'
    + '<h3>This shared link is no longer active</h3>'
    + '<p>The owner has ended access to this preview. Register for a free seat to run GDBS yourself.</p>'
    + '<div class="kiosk-actions"><a class="kiosk-btn-primary" href="' + regUrl() + '">Register</a></div>'
    + '</div>';
  document.body.appendChild(o);
}

function showLockScreen(msg) {
  document.documentElement.classList.add('kiosk-mode');
  const o = document.createElement('div');
  o.className = 'kiosk-overlay kiosk-hard';
  o.innerHTML =
    '<div class="kiosk-card">'
    + '<i class="fas fa-link-slash kiosk-ic"></i>'
    + '<h3>' + esc(msg) + '</h3>'
    + '<p>Register for a free seat to run GDBS yourself.</p>'
    + '<div class="kiosk-actions"><a class="kiosk-btn-primary" href="' + regUrl() + '">Register</a></div>'
    + '</div>';
  document.body.appendChild(o);
}

function showSplash() {
  if (document.getElementById('kiosk-splash')) return;
  const s = document.createElement('div');
  s.id = 'kiosk-splash'; s.className = 'kiosk-overlay';
  s.innerHTML = '<div class="kiosk-card"><i class="fas fa-spinner fa-spin kiosk-ic"></i><h3>Loading shared preview...</h3></div>';
  document.body.appendChild(s);
}
function hideSplash() { const s = document.getElementById('kiosk-splash'); if (s) s.remove(); }

function injectStyles() {
  if (document.getElementById('kiosk-styles')) return;
  const css = `
  .kiosk-mode #landing-screen, .kiosk-mode #auth-screen { display:none !important; }
  .kiosk-locked { opacity:0.85; cursor:not-allowed !important; }
  #kiosk-banner { position:fixed; top:0; left:0; right:0; z-index:9000; display:flex; align-items:center; gap:14px;
    padding:8px 16px; background:#0f172a; color:#e2e8f0; border-bottom:1px solid #7c3aed; font-size:13px; box-shadow:0 2px 10px rgba(0,0,0,.25); }
  .kiosk-has-banner #app-screen { padding-top:42px; }
  #kiosk-banner .kiosk-tag { font-weight:700; color:#a78bfa; }
  #kiosk-banner .kiosk-label { color:#94a3b8; }
  #kiosk-banner .kiosk-cta { margin-left:auto; background:#7c3aed; color:#fff; padding:5px 14px; border-radius:6px; text-decoration:none; font-weight:600; }
  #kiosk-banner .kiosk-cta:hover { background:#6d28d9; }
  .kiosk-overlay { position:fixed; inset:0; z-index:9500; display:flex; align-items:center; justify-content:center; background:rgba(2,6,23,.72); backdrop-filter:blur(3px); }
  .kiosk-overlay.hidden { display:none; }
  .kiosk-overlay.kiosk-hard { background:rgba(2,6,23,.92); }
  .kiosk-card { background:#0f172a; border:1px solid #334155; border-radius:14px; padding:30px 34px; max-width:440px; text-align:center; color:#e2e8f0; box-shadow:0 20px 60px rgba(0,0,0,.5); }
  .kiosk-card .kiosk-ic { font-size:30px; color:#a78bfa; margin-bottom:12px; }
  .kiosk-card h3 { margin:0 0 8px; font-size:18px; color:#f1f5f9; }
  .kiosk-card p { margin:0 0 18px; font-size:13px; color:#94a3b8; line-height:1.5; }
  .kiosk-actions { display:flex; gap:10px; justify-content:center; }
  .kiosk-btn-primary { background:#7c3aed; color:#fff; padding:9px 18px; border-radius:8px; text-decoration:none; font-weight:600; border:none; cursor:pointer; }
  .kiosk-btn-primary:hover { background:#6d28d9; }
  .kiosk-btn { background:transparent; color:#cbd5e1; padding:9px 16px; border-radius:8px; border:1px solid #334155; cursor:pointer; }
  .kiosk-btn:hover { border-color:#475569; }`;
  const s = document.createElement('style'); s.id = 'kiosk-styles'; s.textContent = css; document.head.appendChild(s);
}

// ---- small utils ------------------------------------------------------------
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
function cssId(s) { try { return (window.CSS && CSS.escape) ? CSS.escape(s) : String(s).replace(/[^\w-]/g, '\\$&'); } catch { return s; } }
function cssAttr(s) { return String(s).replace(/["\\]/g, '\\$&'); }
